import { Link } from 'react-router-dom'
import { useMemo, useState, useEffect } from 'react'
import { useAccompagnements } from '@/hooks/useAccompagnements'
import {MapPin, Calendar as CalendarIcon, Users, Sparkles} from 'lucide-react'
import { getCachedText, preloadSiteContent } from '@/hooks/useSiteContent'
import EditableText from '@/components/EditableText'

export default function Accompagnements() {
  const { accompagnements, sessions, loading } = useAccompagnements()
  const [, setTick] = useState(0)

  useEffect(() => {
    preloadSiteContent()
    const handler = () => setTick((n) => n + 1)
    window.addEventListener('site-content-updated', handler)
    return () => window.removeEventListener('site-content-updated', handler)
  }, [])

  const t = (key: string, fallback: string) => getCachedText('accompagnements', key, fallback)

  const offresActives = useMemo(() => {
    return [...accompagnements]
      .filter((a) => a.actif !== false)
      .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
  }, [accompagnements])

  const sessionsParAccompagnement = useMemo(() => {
    const map: Record<string, typeof sessions> = {}
    const now = new Date()
    sessions.forEach((s) => {
      if (!s.accompagnementId) return
      if (s.statut !== 'ouverte') return
      if (s.dateDebut && new Date(s.dateDebut) < now) return
      if (!map[s.accompagnementId]) map[s.accompagnementId] = []
      map[s.accompagnementId].push(s)
    })
    // sort by date
    Object.keys(map).forEach((k) => {
      map[k] = map[k].sort((a, b) => new Date(a.dateDebut).getTime() - new Date(b.dateDebut).getTime())
    })
    return map
  }, [sessions])

  const formatDate = (iso?: string) => {
    if (!iso) return ''
    try {
      return new Date(iso).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    } catch {
      return iso
    }
  }

  return (
    <div className="fade-in">
      {/* Intro */}
      <section className="px-6 py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <EditableText pageKey="accompagnements" sectionKey="hero_eyebrow" fallback="— Temps partagés —" as="p" contentType="subtitle" className="text-xs tracking-[0.4em] uppercase text-[#c9a96e] mb-8" />
          <h1 className="font-serif text-5xl md:text-7xl font-light text-[#f5f1ea] mb-10">
            <EditableText pageKey="accompagnements" sectionKey="hero_title" fallback="Accompagnements" as="span" contentType="title" />
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-10" />
          <EditableText
            pageKey="accompagnements"
            sectionKey="hero_subtitle"
            fallback={"Des espaces dédiés,\npour traverser ensemble ce qui demande du temps."}
            as="p"
            contentType="paragraph"
            multiline
            className="font-serif text-xl md:text-2xl text-[#f5f1ea]/80 leading-loose italic whitespace-pre-line"
          />
        </div>
      </section>

      {/* Offres */}
      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <EditableText pageKey="accompagnements" sectionKey="loading_text" fallback="Chargement des accompagnements…" as="p" contentType="paragraph" className="text-center text-[#a8a39e] font-light italic" />
          ) : offresActives.length === 0 ? (
            <EditableText pageKey="accompagnements" sectionKey="empty_text" fallback="Aucun accompagnement proposé pour le moment." as="p" contentType="paragraph" className="text-center text-[#a8a39e] font-light italic" />
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {offresActives.map((offre) => {
                const offreSessions = offre._id ? (sessionsParAccompagnement[offre._id] || []) : []
                const nbDates = offreSessions.length

                // Prix minimum parmi les sessions disponibles
                const prixSessions = offreSessions
                  .map((s) => Number(s.prix) || 0)
                  .filter((p) => p > 0)
                const prixMin = prixSessions.length > 0 ? Math.min(...prixSessions) : 0

                // Lieux distincts parmi les sessions
                const lieuxDistincts = Array.from(
                  new Set(offreSessions.map((s) => s.lieu).filter(Boolean))
                ) as string[]

                return (
                  <div
                    key={offre._id}
                    className={`relative flex flex-col p-10 border transition-all duration-700 ${
                      offre.miseEnAvant
                        ? 'border-[#c9a96e]/50 bg-[#c9a96e]/[0.03]'
                        : 'border-[#c9a96e]/15 hover:border-[#c9a96e]/40'
                    }`}
                  >
                    <div className="text-center mb-8">
                      {offre.duree && (
                        <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/70 mb-4">
                          {offre.duree}
                        </p>
                      )}
                      <h3 className="font-serif text-3xl text-[#f5f1ea] mb-4">
                        {offre.nom}
                      </h3>
                      <div className="w-8 h-px bg-[#c9a96e]/40 mx-auto mb-6" />

                      {prixMin > 0 ? (
                        <>
                          <EditableText pageKey="accompagnements" sectionKey="prix_from_label" fallback="À partir de" as="p" contentType="paragraph" className="text-[10px] tracking-[0.3em] uppercase text-[#a8a39e] mb-1" />
                          <p className="font-serif text-4xl text-[#c9a96e] mb-2">
                            {prixMin} CHF
                          </p>
                        </>
                      ) : (
                        <EditableText pageKey="accompagnements" sectionKey="prix_sur_demande" fallback="Tarif sur demande" as="p" contentType="paragraph" className="font-serif text-lg text-[#c9a96e]/80 italic mb-2" />
                      )}

                      <p className="text-xs text-[#8a8580] tracking-wide">
                        {nbDates > 0
                          ? `${nbDates} date${nbDates > 1 ? 's' : ''} programmée${nbDates > 1 ? 's' : ''}`
                          : 'Aucune date programmée'}
                      </p>
                    </div>

                    <p className="text-sm text-[#f5f1ea]/75 leading-loose font-light mb-8 text-center italic font-serif">
                      {offre.description}
                    </p>

                    {offre.inclus && offre.inclus.length > 0 && (
                      <ul className="space-y-3 mb-8">
                        {offre.inclus.map((item) => (
                          <li key={item} className="flex items-start gap-3 text-sm text-[#f5f1ea]/70 font-light">
                            <span className="text-[#c9a96e] mt-1.5">◦</span>
                            <span className="leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {offreSessions.length > 0 && (
                      <div className="mb-8 pt-6 border-t border-[#c9a96e]/10">
                        <EditableText pageKey="accompagnements" sectionKey="prochaines_dates_label" fallback="Prochaines dates" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/70 mb-4 text-center" />
                        <ul className="space-y-3">
                          {offreSessions.slice(0, 3).map((s) => (
                            <li key={s._id} className="text-xs text-[#f5f1ea]/75 text-center font-light tracking-wide space-y-1">
                              <div className="flex items-center justify-center gap-2">
                                <CalendarIcon className="w-3 h-3 text-[#c9a96e]/70" />
                                <span>{formatDate(s.dateDebut)}</span>
                              </div>
                              {s.lieu && (
                                <div className="flex items-center justify-center gap-2 text-[#8a8580]">
                                  <MapPin className="w-3 h-3" />
                                  <span>{s.lieu}</span>
                                </div>
                              )}
                              <div className="flex items-center justify-center gap-3 text-[10px]">
                                {s.prix ? (
                                  <span className="text-[#c9a96e]">{s.prix} CHF</span>
                                ) : null}
                                <span className="text-[#c9a96e]/80">
                                  <Users className="w-3 h-3 inline mr-1" />
                                  {s.placesRestantes} place{s.placesRestantes > 1 ? 's' : ''}
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {lieuxDistincts.length > 0 && nbDates > 3 && (
                      <p className="text-[10px] text-[#8a8580] text-center tracking-widest uppercase mb-6">
                        Lieux : {lieuxDistincts.slice(0, 3).join(' · ')}
                        {lieuxDistincts.length > 3 ? '…' : ''}
                      </p>
                    )}

                    {offre.instructionsPreparation && (
                      <div className="mb-8 pt-6 border-t border-[#c9a96e]/10">
                        <EditableText pageKey="accompagnements" sectionKey="pour_se_preparer_label" fallback="Pour se préparer" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/70 mb-4 text-center" />
                        <p className="text-xs text-[#f5f1ea]/65 font-light leading-relaxed text-center italic font-serif whitespace-pre-line">
                          {offre.instructionsPreparation}
                        </p>
                      </div>
                    )}

                    <div className="mt-auto space-y-3">
                      <Link
                        to={nbDates > 0 ? '/contact' : `/contact?organiser=${encodeURIComponent(offre.nom)}`}
                        className={`block w-full py-4 text-center text-xs tracking-[0.3em] uppercase transition-all duration-500 ${
                          offre.miseEnAvant
                            ? 'bg-[#c9a96e] text-[#262626] hover:bg-[#e4c891]'
                            : 'border border-[#c9a96e]/40 text-[#c9a96e] hover:border-[#c9a96e] hover:bg-[#c9a96e]/5'
                        }`}
                      >
                        {nbDates > 0 ? t('btn_reserver', 'Réserver un temps') : t('btn_prendre_contact', 'Prendre contact')}
                      </Link>

                      <Link
                        to={`/contact?organiser=${encodeURIComponent(offre.nom)}`}
                        className="flex items-center justify-center gap-2 w-full py-3 text-center text-[10px] tracking-[0.3em] uppercase text-[#8a8580] hover:text-[#c9a96e] border border-[#c9a96e]/10 hover:border-[#c9a96e]/40 transition-all duration-500 bg-[#c9a96e]/[0.02]"
                      >
                        <Sparkles className="w-3 h-3" />
                        {t('btn_organiser_chez_vous', 'Organiser chez vous')}
                      </Link>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* Organiser chez vous — bloc dédié */}
      <section className="px-6 py-24 border-t border-[#c9a96e]/10 bg-[#c9a96e]/[0.02]">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <EditableText pageKey="accompagnements" sectionKey="organiser_eyebrow" fallback="Vous êtes organisateur" as="p" contentType="subtitle" className="text-xs tracking-[0.4em] uppercase text-[#c9a96e]" />
          <h2 className="font-serif text-3xl md:text-5xl font-light text-[#f5f1ea] leading-tight">
            <EditableText pageKey="accompagnements" sectionKey="organiser_title_1" fallback="Accueillir un accompagnement" as="span" contentType="title" /><br />
            <EditableText pageKey="accompagnements" sectionKey="organiser_title_2" fallback="chez vous" as="span" contentType="title" className="italic text-[#c9a96e]" />
          </h2>
          <div className="w-12 h-px bg-[#c9a96e]/40 mx-auto" />
          <EditableText
            pageKey="accompagnements"
            sectionKey="organiser_text_1"
            fallback={"Centre, studio, lieu de pratique, collectif — si vous souhaitez proposer\nun de ces accompagnements à votre communauté, nous pouvons venir à vous."}
            as="p"
            contentType="paragraph"
            multiline
            className="text-base text-[#f5f1ea]/75 leading-loose font-light max-w-2xl mx-auto font-serif italic whitespace-pre-line"
          />
          <EditableText
            pageKey="accompagnements"
            sectionKey="organiser_text_2"
            fallback="Échangeons sur votre lieu, votre public, le format et les conditions d'organisation."
            as="p"
            contentType="paragraph"
            multiline
            className="text-sm text-[#8a8580] leading-loose font-light max-w-xl mx-auto"
          />
          <div className="pt-4">
            <Link
              to="/contact?organiser=1"
              className="inline-flex items-center gap-3 px-10 py-4 bg-[#c9a96e] text-[#262626] hover:bg-[#e4c891] text-xs tracking-[0.3em] uppercase font-semibold transition-all duration-500"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <EditableText pageKey="accompagnements" sectionKey="organiser_cta" fallback="Proposer mon lieu" as="span" contentType="button" />
            </Link>
          </div>
        </div>
      </section>

      {/* Payment info */}
      <section className="px-6 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <EditableText pageKey="accompagnements" sectionKey="paiement_eyebrow" fallback="Modalités" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]" />
          <h2 className="font-serif text-3xl md:text-4xl font-light text-[#f5f1ea]">
            <EditableText pageKey="accompagnements" sectionKey="paiement_title" fallback="Paiement en ligne sécurisé" as="span" contentType="title" />
          </h2>
          <EditableText
            pageKey="accompagnements"
            sectionKey="paiement_text"
            fallback="La réservation se fait à travers un échange préalable, pour s'assurer que l'accompagnement correspond à ce qui est cherché. Le règlement peut se faire en une ou plusieurs fois, selon ce qui est juste pour toi."
            as="p"
            contentType="paragraph"
            multiline
            className="text-base text-[#a8a39e] leading-loose font-light max-w-2xl mx-auto"
          />

          <div className="flex flex-wrap justify-center gap-6 pt-6">
            <div className="px-6 py-3 border border-[#c9a96e]/20 text-xs tracking-wider text-[#f5f1ea]/70">
              <EditableText pageKey="accompagnements" sectionKey="paiement_mode_1" fallback="Carte bancaire" as="span" contentType="paragraph" />
            </div>
            <div className="px-6 py-3 border border-[#c9a96e]/20 text-xs tracking-wider text-[#f5f1ea]/70">
              <EditableText pageKey="accompagnements" sectionKey="paiement_mode_2" fallback="Virement" as="span" contentType="paragraph" />
            </div>
            <div className="px-6 py-3 border border-[#c9a96e]/20 text-xs tracking-wider text-[#f5f1ea]/70">
              <EditableText pageKey="accompagnements" sectionKey="paiement_mode_3" fallback="Paiement en plusieurs fois" as="span" contentType="paragraph" />
            </div>
          </div>
        </div>
      </section>

      {/* Note */}
      <section className="px-6 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-2xl mx-auto text-center">
          <EditableText
            pageKey="accompagnements"
            sectionKey="note_disclaimer"
            fallback={"Ces accompagnements ne remplacent pas un suivi médical ou psychologique.\nIls proposent un espace d'exploration personnelle, jamais une promesse de guérison."}
            as="p"
            contentType="paragraph"
            multiline
            className="font-serif italic text-lg md:text-xl text-[#f5f1ea]/70 leading-loose whitespace-pre-line"
          />
        </div>
      </section>
    </div>
  )
}
